import React, { useRef, useLayoutEffect, useState } from 'react';
import { connect } from 'react-redux';
import styles from '../Dashboards.module.css';
import classnames from 'classnames';
import {
  updatePanel,
  beginDrag,
  dropPanel,
  deletePanel,
  openPanelModal,
} from '../store/actions';
import MoreMenu from './MoreMenu';
import ResizeHandle from './ResizeHandle';
import PanelDragPreview from './PanelDragPreview';
import {
  getPanelContent,
  getPanelTitleContent,
  clampPanel,
  COLUMN_COUNT,
  ROW_HEIGHT,
  PANEL_GAP,
} from '../helpers';

import { useAutoScroll, usePublishingConfiguration } from '../hooks';
import { useDrag } from 'react-use-gesture';
import { animated, useSpring } from 'react-spring';

// px padding for top/bottom
const PANEL_PADDING_VERTICAL = 8;
// px padding for right/left
const PANEL_PADDING_HORIZONTAL = 12;

interface PanelData {
  id: string;
  type?: string;
  row: string | number;
  column: string | number;
  width: string | number;
  height: string | number;
  [key: string]: any;
}

interface GridBox {
  width: number;
}

interface TypeStyles {
  header: string;
  panel: string;
}

interface PanelProps {
  bookmarkId?: any;
  scrollableRef?: React.RefObject<HTMLElement>;
  gridBoxRef?: React.RefObject<HTMLElement>;
  panel: PanelData;
  deletePanel: (panelId: string) => void;
  preview?: boolean;
  beginDrag: (panelId: string) => void;
  dropPanel: (
    panelId: string,
    options: {
      dragType: string | null;
      rowOffset: number;
      columnOffset: number;
    }
  ) => void;
  updatePanel: (panel: PanelData) => void;
  canEdit?: boolean;
  openPanelModal: (panelId: string) => void;
  rowCount: number;
  isSharedWebpage?: boolean;
}

interface StateProps {
  gridSize: any;
  canEdit: boolean;
  isSharedWebpage: boolean;
}

interface DispatchProps {
  updatePanel: (panel: PanelData) => void;
  beginDrag: (panelId: string) => void;
  dropPanel: (
    panelId: string,
    options: {
      dragType: string | null;
      rowOffset: number;
      columnOffset: number;
    }
  ) => void;
  deletePanel: (panelId: string) => void;
  openPanelModal: (panelId: string) => void;
}

const getTypeStyles = (panel?: PanelData): TypeStyles => {
  switch (panel?.type) {
    case 'SectionHeaderPanel': {
      return {
        header: styles.SectionHeader,
        panel: styles.SectionHeaderPanel,
      };
    }
    case 'RichTextPanel': {
      return {
        header: styles.RichTextHeader,
        panel: styles.RichTexPanel,
      };
    }
    default: {
      return {
        header: styles.PanelHeader,
        panel: styles.Panel,
      };
    }
  }
};

const onEditPanel = (
  e: React.MouseEvent,
  panel: PanelData,
  headerRef: React.RefObject<HTMLElement>,
  openPanelModal: (panelId: string) => void
): void => {
  switch (panel?.type) {
    case 'SectionHeaderPanel': {
      // Mimic clicking the react-in-place input
      // eslint-disable-next-line custom-aha-rules/require-scope-in-query-selector
      const input = headerRef?.current?.querySelector(
        '.SectionHeaderInput'
      ) as HTMLElement;
      if (input) {
        input.click();
        input.focus();
      }
      break;
    }
    default: {
      openPanelModal(panel.id);
    }
  }
};

function Panel({
  bookmarkId,
  scrollableRef,
  gridBoxRef,
  panel,
  deletePanel,
  preview,
  beginDrag,
  dropPanel,
  updatePanel,
  canEdit,
  openPanelModal,
  rowCount,
  isSharedWebpage,
}: PanelProps): JSX.Element {
  const headerRef = useRef<HTMLDivElement>(null);
  const { publishing } = usePublishingConfiguration();
  const { row, column, width, height } = clampPanel({
    row: parseInt(panel.row as string),
    column: parseInt(panel.column as string),
    width: parseInt(panel.width as string),
    height: parseInt(panel.height as string),
    rowCount,
  });

  const [gridBox, setGridBox] = useState<GridBox>({ width: 0 });
  const [initialScroll, setInitialScroll] = useState<number>(0);
  const [dragType, setDragType] = useState<string | null>(null);

  const [{ movement, gridOffsets }, set] = useSpring(() => ({
    movement: [0, 0],
    gridOffsets: [0, 0],
  }));

  const { autoScroll, startAutoScroll, endAutoScroll } = useAutoScroll(
    scrollableRef,
    {
      triggerAt: 50,
      threshold: 5,
      velocity: 8,
    }
  );

  const bindDragHandlers = useDrag(
    ({
      event,
      first,
      last,
      xy: [, clientY],
      movement: [mx, my],
      down,
      cancel,
      canceled,
      args,
    }) => {
      const [{ type }] = args || [{ type: 'move' }];
      if (preview || canceled) return;
      if (
        (first &&
          (event?.target as HTMLElement)?.classList?.contains(
            'react-in-place__input'
          )) ||
        isSharedWebpage ||
        !canEdit
      )
        return cancel();

      const columnOffset = Math.round(mx / (gridBox.width / COLUMN_COUNT)) || 0;
      const rowOffset =
        Math.round((my + getScrollOffset()) / (ROW_HEIGHT + PANEL_GAP)) || 0;

      if (first) {
        beginDrag(panel.id);
        setGridBox(
          gridBoxRef?.current?.getBoundingClientRect() || { width: 0 }
        );
        setInitialScroll(scrollableRef?.current?.scrollTop || 0);
        startAutoScroll(clientY);
      } else if (last) {
        endAutoScroll();
        setInitialScroll(0);
        setGridBox({ width: 0 });
        dropPanel(panel.id, { dragType, rowOffset, columnOffset });
        // Some bookmarks have onResize events that don't
        // work well when the panel is being resized,
        // dispatching a window resize event solves the issue.
        window.requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
        });
      }

      setDragType(down ? type : null);
      autoScroll(clientY);
      void set({
        immediate: true,
        movement: down ? [mx, my] : [0, 0],
        gridOffsets: down ? [columnOffset, rowOffset] : [0, 0],
      });
    }
  );

  // This prevents an awkward bug that occurs when you drag a panel to one of
  // the provisional rows that only exist while dragging. If one of the
  // provisional rows would cause the grid's container to be scrollable, we need
  // to adjust its scroll position after the panel is dropped. If we do not do
  // this, the grid's container will scroll back to its previous position the
  // next time the user starts to reposition a panel.
  useLayoutEffect(() => {
    const currentScroll = scrollableRef?.current?.scrollTop;

    if (!dragType && currentScroll && scrollableRef?.current) {
      scrollableRef.current.scrollTop = currentScroll - 1;
      scrollableRef.current.scrollTop = currentScroll;
    }
  }, [scrollableRef, dragType]);

  function getScrollOffset(): number {
    return dragType
      ? (scrollableRef?.current?.scrollTop || 0) - initialScroll
      : 0;
  }

  const style: any = {
    gridColumn: `${column} / span ${width}`,
    gridRow: `${row} / span ${height}`,
  };

  if (dragType === 'move') {
    style.transform = movement.interpolate(
      (x: number, y: number) =>
        `translateX(${x}px) translateY(${y + getScrollOffset()}px)`
    );
  } else if (dragType === 'resize') {
    // Width of a single column, accounting for the gaps between them.
    style.minWidth =
      (gridBox.width - (COLUMN_COUNT - 1) * PANEL_GAP) / COLUMN_COUNT;

    style.width = movement.interpolate((dx: number) => `calc(100% + ${dx}px)`);
    style.height = movement.interpolate(
      (_: number, dy: number) => `calc(100% + ${dy + getScrollOffset()}px)`
    );
  }

  const panelContent = getPanelContent(panel, preview, width, height, [
    PANEL_PADDING_HORIZONTAL,
    PANEL_PADDING_VERTICAL,
  ]);

  const typeStyles = getTypeStyles(panel);

  return (
    <React.Fragment>
      <animated.div
        suppressHydrationWarning
        id={`Dashboard-Panel-${panel.id}`}
        className={classnames(typeStyles.panel, {
          [styles.PanelDragged]: Boolean(dragType),
          [styles.PanelPublishing]: publishing,
        })}
        style={style}
      >
        <div className={classnames(typeStyles.header)} ref={headerRef}>
          <div
            {...bindDragHandlers({ type: 'move' })}
            className={classnames(styles.PanelDragHandle, {
              [styles.PanelPublishing]: publishing,
              [styles.PanelCanEdit]: canEdit,
            })}
          >
            <span suppressHydrationWarning className={styles.PanelTitle}>
              {getPanelTitleContent(panel, updatePanel)}
            </span>
          </div>
          {!publishing && (
            <MoreMenu
              {...({
                bookmarkId,
                panel,
                preview,
                editPanel: (e: React.MouseEvent, panel: PanelData) =>
                  onEditPanel(e, panel, headerRef, openPanelModal),
                deletePanel,
                openPanelModal,
                canEdit,
              } as any)}
            />
          )}
        </div>
        {panelContent}
        {!preview && canEdit && !publishing && (
          <div className={styles.PanelResizeContainer}>
            <div
              className={styles.PanelResize}
              style={{ touchAction: 'manipulation' }}
              {...bindDragHandlers({ type: 'resize' })}
            >
              <ResizeHandle ariaLabel='Resize panel' />
            </div>
          </div>
        )}
      </animated.div>
      <PanelDragPreview
        dragType={dragType}
        gridOffsets={gridOffsets}
        dimensions={{ row, column, width, height }}
        rowCount={rowCount}
      />
    </React.Fragment>
  );
}

const mapStateToProps = (state: any): StateProps => ({
  gridSize: state.getIn(['meta', 'gridSize']),
  canEdit: state.getIn(['meta', 'canEdit']),
  isSharedWebpage: state.getIn(['meta', 'isSharedWebpage']),
});

const mapDispatchToProps: DispatchProps = {
  updatePanel,
  beginDrag,
  dropPanel,
  deletePanel,
  openPanelModal,
};

export default connect<StateProps, DispatchProps, PanelProps>(
  mapStateToProps,
  mapDispatchToProps
)(Panel);
